import React, { useCallback, useEffect, useRef, useState } from 'react'
import * as Sentry from '@sentry/react'
import { useIntl } from 'react-intl'
import { Button, Result, Space, Spin, theme } from 'antd'
import { RocketOutlined } from '@ant-design/icons'
import { useClearBrowserCache } from 'react-clear-browser-cache'
import { isChunkLoadError } from 'utils/helper'
import {
  getChunkReloadKey,
  getFailedChunkUrl,
  getHashPathname,
  refreshBrokenChunks,
} from 'utils/appUpdate'
import { reportToSentry } from 'utils/sentry'
import ClearCacheGuideModal from './ClearCacheGuideModal'
import styles from './ChunckErrorFallback.module.scss'

const MAX_RELOAD_ATTEMPTS = 3
const RELOAD_KEY = getChunkReloadKey() // per-tab (sessionStorage), per build
// Jumlah klik "Coba Lagi" di tab ini. Kalau user kembali ke layar ini, semua klik itu gagal.
const MANUAL_RETRY_KEY = `chunkManualRetry:${import.meta.env.PACKAGE_VERSION}`
// Setelah 2 klik gagal, klik berikutnya menampilkan panduan hapus cache
const GUIDE_AFTER_FAILED_RETRIES = 2

const getManualRetryCount = () => parseInt(sessionStorage.getItem(MANUAL_RETRY_KEY) || '0', 10)

export default function ChunkErrorFallback({ error, resetError }) {
  const { formatMessage } = useIntl()
  const { token } = theme.useToken()
  const { clearCacheAndReload } = useClearBrowserCache()
  const triedOnceRef = useRef(false)
  // true saat reload otomatis tidak bisa jalan (offline / jatah percobaan habis)
  const [needManualRetry, setNeedManualRetry] = useState(false)
  const [guideOpen, setGuideOpen] = useState(false)

  const doReload = useCallback(async () => {
    // hindari reload saat offline
    if (!navigator.onLine) {
      setNeedManualRetry(true)
      return
    }

    // guard attempts per tab
    const attempts = parseInt(sessionStorage.getItem(RELOAD_KEY) || '0', 10)
    if (attempts >= MAX_RELOAD_ATTEMPTS) {
      setNeedManualRetry(true)
      // User tersangkut di layar "Coba Lagi". Chunk error biasa dibuang lewat ignoreErrors,
      // jadi kirim laporan terpisah supaya kasus ini terlihat di Sentry.
      reportToSentry({
        message: `Chunk gagal dimuat setelah ${MAX_RELOAD_ATTEMPTS} kali reload`,
        level: 'fatal',
        fingerprint: ['chunk-load-stuck'],
        context: {
          name: 'chunk_load_stuck',
          context: {
            chunkUrl: getFailedChunkUrl(error),
            errorMessage: String(error?.message || error),
            attempts,
            manualRetries: getManualRetryCount(),
            pathname: getHashPathname(),
          },
        },
      })
      return
    }
    setNeedManualRetry(false)
    sessionStorage.setItem(RELOAD_KEY, String(attempts + 1))

    try {
      // beri waktu Sentry kirim event, sambil minta ulang chunk yang cache-nya rusak
      await Promise.all([Sentry.flush(1500), refreshBrokenChunks(error)])
    } catch (_) {
      /* noop */
    }

    try {
      // prefer bersihkan cache RCBC lalu reload
      clearCacheAndReload()
    } catch {
      window.location.reload()
    }
  }, [clearCacheAndReload, error])

  // 🔹 Pindahkan side-effect ke sini, bukan di render
  useEffect(() => {
    if (isChunkLoadError(error) && !triedOnceRef.current) {
      triedOnceRef.current = true
      doReload()
    }
  }, [error, doReload])

  const manualRetry = useCallback(() => {
    // saat offline tidak ada reload, jadi tidak dihitung sebagai klik gagal
    if (navigator.onLine) {
      sessionStorage.setItem(MANUAL_RETRY_KEY, String(getManualRetryCount() + 1))
    }
    sessionStorage.removeItem(RELOAD_KEY)
    triedOnceRef.current = false
    doReload()
  }, [doReload])

  const retryHandler = useCallback(() => {
    if (getManualRetryCount() >= GUIDE_AFTER_FAILED_RETRIES) {
      setGuideOpen(true)
      return
    }
    manualRetry()
  }, [manualRetry])

  const guideRetryHandler = useCallback(() => {
    setGuideOpen(false)
    manualRetry()
  }, [manualRetry])

  const guideCloseHandler = useCallback(() => {
    setGuideOpen(false)
  }, [])

  const resetHandler = useCallback(() => {
    sessionStorage.removeItem(RELOAD_KEY)
    triedOnceRef.current = false
    resetError()
  }, [resetError])

  if (isChunkLoadError(error)) {
    // Ini bukan error sungguhan, hanya versi baru yang sedang dipasang — tampilkan layar tenang
    let subTitle = formatMessage({ id: 'notification.update_installing_description' })
    if (needManualRetry) {
      subTitle = formatMessage({
        id: navigator.onLine
          ? 'notification.update_installing_failed'
          : 'notification.update_installing_offline',
      })
    }

    return (
      <>
        <Result
          icon={
            <span
              className={styles.rocket}
              data-active={!needManualRetry}
              style={{
                '--rocket-color': token.colorPrimary,
                '--rocket-halo': token.colorPrimaryBg,
                '--rocket-flame-inner': token.colorWarning,
                '--rocket-flame-outer': token.colorError,
              }}
            >
              <span className={styles.pulse} />
              <span className={styles.trail} />
              <span className={styles.trail} />
              <span className={styles.trail} />
              <span className={styles.ship}>
                <RocketOutlined />
                <span className={styles.flame} />
              </span>
            </span>
          }
          title={formatMessage({ id: 'notification.update_installing' })}
          subTitle={subTitle}
          extra={
            needManualRetry ? (
              <Space>
                <Button type="primary" onClick={retryHandler}>
                  {formatMessage({ id: 'button.retry' })}
                </Button>
                <Button onClick={resetHandler}>{formatMessage({ id: 'settings.reset' })}</Button>
              </Space>
            ) : (
              <Spin />
            )
          }
        />
        <ClearCacheGuideModal
          open={guideOpen}
          onRetry={guideRetryHandler}
          onClose={guideCloseHandler}
        />
      </>
    )
  }

  // fallback umum non-chunk
  return (
    <Result
      status="500"
      title="Maaf, Terjadi masalah pada aplikasi Kledo!"
      subTitle="Silakan klik Reload untuk memuat ulang halaman, atau Reset untuk mencoba lagi."
      extra={[
        <Button type="primary" key="reload" onClick={() => window.location.reload()}>
          Reload
        </Button>,
        <Button key="reset" onClick={resetError}>
          Reset
        </Button>,
      ]}
    />
  )
}
